import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {debounceTime, finalize, map, Observable, of, Subject, Subscription, switchMap} from 'rxjs';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {environment} from 'src/environments/environment';
import {DropdownSelectCoordinatorService} from './dropdown-select-coordinator.service';

@Component({
    selector: 'app-dropdown-select',
  templateUrl: './dropdown-select.component.html',
  styleUrl: './dropdown-select.component.scss',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownSelectComponent),
      multi: true,
    },
  ],
})
export default class DropdownSelectComponent
  implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy, OnChanges
{
  private static nextCoordInstanceId = 0;

  /** Identifiant pour fermer les autres listes lorsqu’une s’ouvre. */
  private readonly coordInstanceId = `ds-${DropdownSelectComponent.nextCoordInstanceId++}`;

  @Input() apiFilters: Record<string, any> = {};
  @Input() apiUrl: string = ''; // Liste d'éléments distant
  @Input() design: string = 'standard'; // Liste d'éléments distant
  @Input() apiFilterKeys: any = {}; // filtre Liste d'éléments distant
  @Input() inputItems: any[] = []; // Liste des éléments passée en entrée. Remplace apiUrl.
  @Input() placeHolder: string = 'Sélectionnez un élément...';
  @Input() searchKey: string = ''; // filtre de l'api pour la recherche
  @Input() itemValueKey: string = ''; // valeur de chaque élément
  @Input() itemLabelKey: string = ''; // label a afficher pour chaque élément
  @Input() isMultiSelect: boolean = false;
  @Input() itemsPerPage: number = 10;
  @Input() maxHeight: number = 400;
  @Input() defaultValue: any;
  @Input() returnFullItem: boolean = false;
  @Input() needResetSelectedValue: boolean = false; // Réinitialise la recherche
  @Input() hideValidateButton: boolean = false; // Masquer le bouton "Valider la sélection"
  @Input() capitalizeLabel: boolean = false; // Capitaliser la première lettre des labels
  @Input() excludeIds: string[] = []; // IDs à exclure de la liste des options
  @Input() optionDisabledKey: string = 'disabled';
  @Input() optionIconClassKey: string = 'iconClass';
  @Input() optionIconColorKey: string = 'iconColor';
  /**
   * Sur `user_firms`, n'applique pas le filtre actif par défaut (`userInfo.isEnabled=true`).
   * À utiliser uniquement si l'écran doit lister aussi les collaborateurs désactivés.
   */
  @Input() includeInactiveUserFirms: boolean = false;
  /** Ouvre automatiquement le dropdown après initialisation */
  @Input() autoOpen: boolean = false;
  /** Appliquer | translate aux labels des items (utile quand les labels sont des clés i18n) */
  @Input() translateLabels: boolean = false;
  /** Ouvrir la liste vers le haut plutôt que vers le bas */
  @Input() dropUp: boolean = false;
  /** Active l'ouverture automatique vers le haut s'il manque de place en bas. */
  @Input() autoDropUp: boolean = true;
  @Input() inlineList: boolean = false;
  @Output() onSelectionChange = new EventEmitter<any>();
  @Output() onDisabledSelection = new EventEmitter<any>();
  /** Émis uniquement quand l'utilisateur valide sa sélection (clic sur "Valider" ou clic hors dropdown en multi-select) */
  @Output() onValidateSelection = new EventEmitter<void>();

  isDisabled: boolean = false;
  isFieldsetDisabled: boolean = false;
  private observer: MutationObserver | null = null;

  page: number = 1;
  isDropdownVisible = false; // Contrôle si la liste déroulante est visible ou non
  items: any[] = []; // La liste des éléments
  filteredItems: any[] = []; // Liste des éléments filtrés en fonction de la recherche
  selectedItemList: any[] = [];
  totalItems: number = 0;
  searchValue: string = '';
  hasChangeSelection: boolean = false;
  isLoading: boolean = false;
  computedDropUp: boolean = false;
  computedMaxHeight: number = 400;
  subscription: Subscription = new Subscription();
  @ViewChildren('searchInput')
  private searchInputs!: QueryList<ElementRef<HTMLInputElement>>;
  private outsidePointerDownHandler: ((event: Event) => void) | null = null;

  private elementRef = inject(ElementRef); // Injecte la référence de l'élément DOM du composant
  private searchSubject = new Subject<string>();
  private isSearchInitialized = false;
  private _value: any; // Valeur interne formControl
  private readonly selectedUserFirmRequests = new Set<string>();
  private _isInitialized: boolean = false; // Flag to track initialization

  constructor(
    private http: HttpClient,
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
    private dropdownCoordinator: DropdownSelectCoordinatorService
  ) {}

  formatApiUrl(): void {
    if (!this.apiUrl) return;
    try {
      new URL(this.apiUrl);
    } catch {
      this.apiUrl = environment.apiUrl + '/api/' + this.apiUrl;
    }
  }

  searchElementWithApi(): void {
    if (this.isSearchInitialized || !this.apiUrl) {
      return;
    }
    this.isSearchInitialized = true;
    this.subscription.add(
      this.searchSubject
        .pipe(
          debounceTime(300),
          switchMap((query) => this.fetchOptions(query))
        )
        .subscribe((response) => {
          this.isLoading = false;
          this.filteredItems = response.options || [];
          this.updateDropdownSelectedValuesIfsearchIsReset();
          this.updateDropdownDirection();
        })
    );
  }

  updateDropdownSelectedValuesIfsearchIsReset() {
    if (this.searchValueIsReset()) {
      this.updateFilteredItems();
    }
  }

  searchValueIsReset(): boolean {
    return this.searchValue.trim().length === 0;
  }

  hasInputItems(): boolean {
    return this.inputItems?.length > 0;
  }

  get value(): any {
    return this._value;
  }

  set value(val: any) {
    this._value = val;
    // Notifie le formulaire lors d'un changement de valeur seulement après l'initialisation
    if (this._isInitialized && this.onChange) {
      if (this.isMultiSelect) {
        this.onChange(val);
      } else {
        this.onChange(val[0]);
      }
    }
  }

  // Méthodes pour `ControlValueAccessor`
  writeValue(value: any): void {
    if (value === null || value === '') {
      this._value = [];
      if (this.filteredItems?.length) {
        this.filteredItems = this.filteredItems.map((item) => ({
          ...item,
          selected: false,
        }));
      }
      this.items = [];
      return;
    }
    if (value !== undefined) {
      // Set value without triggering onChange during initialization
      this._value = this.transformInArray(value);
      this.setDefaultValue();
    }
  }

  registerOnChange(fn: any): void {
    // Wrapper pour émettre à la fois vers ControlValueAccessor ET vers onSelectionChange
    this.onChange = (value: any) => {
      fn(value); // Appeler la fonction du ControlValueAccessor
      this.onSelectionChange.emit(value); // Émettre aussi l'événement @Output
    };
    this._isInitialized = true;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Optionnel : Gérer l'état désactivé si nécessaire
    this.isDisabled = isDisabled;
  }

  // Méthode pour notifier le formulaire
  private onChange: (value: any) => void = (value: any) => {
    // Toujours émettre via onSelectionChange, même si pas dans un formulaire réactif
    this.onSelectionChange.emit(value);
  };

  // Méthode pour marquer le champ comme touché
  private onTouched: () => void = () => {};

  // Au moment de l'initialisation du composant
  ngOnInit() {
    this.formatApiUrl();
    // Initialiser _isInitialized à true par défaut pour permettre l'émission d'événements
    // même sans ControlValueAccessor (quand utilisé avec @Output onSelectionChange)
    this._isInitialized = true;

    if (this.inputItems?.length > 0) {
      // Utilise les éléments passés via @Input()
      this.initializeFromInputItems();
    } else if (this.apiUrl) {
      this.searchElementWithApi();
      // Sinon, récupère les options via l'API
      this.subscription.add(
        this.fetchOptions().subscribe({
          next: (response) => this.fetchOptionsSuccess(response),
          error: () => this.fetchOptionsError(),
        })
      );
    }
    if (this.hasDefautValues()) {
      // Assignation directe sur _value pour éviter de déclencher onChange (et donc
      // onSelectionChange) lors de l'initialisation. Le setter `value` appelle onChange
      // dès que _isInitialized est true, ce qui provoquerait la fermeture immédiate
      // du chip-panel quand des valeurs sont déjà sélectionnées.
      // Utiliser nullish coalescing (??) au lieu de || pour que false/0 soient
      // traités comme des valeurs valides (|| court-circuite sur toute valeur falsy).
      this._value = this.transformInArray(this.defaultValue ?? this._value);
      this.setDefaultValue();
    }
    this.observeFieldsetDisabled();

    this.subscription.add(
      this.dropdownCoordinator.openedInstanceId$.subscribe((id) => {
        if (id === this.coordInstanceId || !this.isDropdownVisible) {
          return;
        }
        if (this.isMultiSelect && this.hasChangeSelection) {
          this.confirmSelection();
        } else {
          this.isDropdownVisible = false;
          this.resetSearch();
        }
      })
    );

    this.bindOutsidePointerDown();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['apiFilters'] && !changes['apiFilters'].firstChange) {
      this.searchElementWithApi();
      this.subscription.add(
        this.fetchOptions().subscribe({
          next: (response) => this.fetchOptionsSuccess(response),
          error: () => this.fetchOptionsError(),
        })
      );
    }
    if (changes['apiFilterKeys'] && !changes['apiFilterKeys'].firstChange) {
      this.searchElementWithApi();
      this.subscription.add(
        this.fetchOptions().subscribe({
          next: (response) => this.fetchOptionsSuccess(response),
          error: () => this.fetchOptionsError(),
        })
      );
    }
    if (changes['inputItems'] && !changes['inputItems'].firstChange) {
      this.initializeFromInputItems();
    } else if (changes['apiUrl'] && !changes['apiUrl'].firstChange) {
      this.formatApiUrl();
      this.searchElementWithApi();
      this.subscription.add(
        this.fetchOptions().subscribe({
          next: (response) => this.fetchOptionsSuccess(response),
          error: () => this.fetchOptionsError(),
        })
      );
    }
    if (
      changes['needResetSelectedValue'] &&
      !changes['needResetSelectedValue'].firstChange
    ) {
      this.resetSelectedValue();
    }
    if (changes['defaultValue'] && !changes['defaultValue'].firstChange) {
      const newDefault = this.defaultValue;
      if (newDefault === null || newDefault === undefined || newDefault === '') {
        this._value = [];
        this.items = this.items.map((item) => ({ ...item, selected: false }));
        this.filteredItems = this.filteredItems.map((item) => ({ ...item, selected: false }));
      } else {
        this._value = this.transformInArray(newDefault);
        this.setDefaultValue();
      }
      this.cdr.markForCheck();
    }
  }

  hasDefautValues(): boolean {
    return this.defaultValue !== undefined && this.defaultValue !== null;
  }

  // On traite tout en tableau pour la multi sélection
  transformInArray(value: any) {
    return Array.isArray(value) ? value : [value];
  }

  // Initialise les éléments et les éléments filtrés à partir de `inputItems`
  initializeFromInputItems(): void {
    this.items = this.inputItems.map((item) => ({
      value: item[this.itemValueKey],
      label: this.formatLabel(item[this.itemLabelKey]),
      FullItem: { ...item },
      selected: this.formControlValuesInclude(item[this.itemValueKey]),
      disabled: !!item[this.optionDisabledKey],
      iconClass: this.itemIconClass(item),
      iconColor: this.itemIconColor(item),
    }));
    this.filteredItems = [...this.items];
    this.totalItems = this.items.length;
  }

  // Formate le label (capitalise la première lettre si demandé)
  formatLabel(label: string): string {
    if (!label) return '';
    if (this.capitalizeLabel) {
      return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
    }
    return label;
  }

  fetchOptions(query: string = ''): Observable<{ options: any[]; totalItems: number }> {
    this.isLoading = true;
    if (this.isUserFirmsResourceUrl()) {
      return this.fetchAllUserFirmOptions(query);
    }

    return this.fetchOptionsPage(query, this.page, this.itemsPerPage).pipe(
      map((response) => this.responseToOptions(response))
    );
  }

  private fetchAllUserFirmOptions(
    query: string,
    page: number = 1,
    previousOptions: any[] = []
  ): Observable<{ options: any[]; totalItems: number }> {
    const itemsPerPage = Math.max(this.itemsPerPage, 100);
    return this.fetchOptionsPage(query, page, itemsPerPage).pipe(
      switchMap((response) => {
        const pageOptions = this.responseToOptions(response).options;
        const options = [...previousOptions, ...pageOptions];
        const total = response?.totalItems ?? response?.['hydra:totalItems'];
        const hasNextPage = total
          ? page * itemsPerPage < total
          : pageOptions.length === itemsPerPage;

        if (hasNextPage) {
          return this.fetchAllUserFirmOptions(query, page + 1, options);
        }

        return of({
          options: this.sortOptionsIfUserFirms(options),
          totalItems: options.length,
        });
      })
    );
  }

  private fetchOptionsPage(query: string, page: number, itemsPerPage: number): Observable<any> {
    const queryParams = this.buildQueryParams(query);
    const apiFilters = Object.entries(this.mergeApiFiltersForRequest())
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    const url =
      `${this.apiUrl}?page=${page}&itemsPerPage=${itemsPerPage}` +
      (queryParams ? `&${queryParams}` : '') +
      (apiFilters ? `&${apiFilters}` : '');

    return this.http.get<any>(url);
  }

  private responseToOptions(response: any): { options: any[]; totalItems: number } {
    const member = response?.['hydra:member'] ?? response?.['member'];
    if (!Array.isArray(member)) {
      return { options: [], totalItems: 0 };
    }

    const filteredMember = this.filterExcludedItems(member);
    const options = filteredMember.map((item) => ({
      value: item[this.itemValueKey],
      label: this.formatLabel(item[this.itemLabelKey]),
      FullItem: { ...item },
      selected: this.formControlValuesInclude(item[this.itemValueKey]),
      disabled: !!item[this.optionDisabledKey],
      iconClass: this.itemIconClass(item),
      iconColor: this.itemIconColor(item),
    }));

    return {
      options: this.sortOptionsIfUserFirms(options),
      totalItems: options.length,
    };
  }

  private filterExcludedItems(items: any[]): any[] {
    if (!this.excludeIds || this.excludeIds.length === 0) {
      return items;
    }

    return items.filter((item: any) => {
      const itemId = item[this.itemValueKey];
      if (!itemId) return true;

      const itemIdStr = typeof itemId === 'string' && itemId.includes('/')
        ? itemId.split('/').pop() || ''
        : String(itemId);
      const directId = item.id ? String(item.id) : null;

      return !this.excludeIds.includes(itemIdStr) &&
        !this.excludeIds.includes(String(itemId)) &&
        (!directId || !this.excludeIds.includes(directId));
    });
  }

  /** True si la requête cible la collection `user_firms` (URL relative ou absolue). */
  private isUserFirmsResourceUrl(): boolean {
    return !!this.apiUrl && this.apiUrl.includes('/user_firms');
  }

  private sortOptionsIfUserFirms(options: any[]): any[] {
    if (!this.isUserFirmsResourceUrl()) return options;
    return [...options].sort((a, b) =>
      String(a.label ?? '').localeCompare(String(b.label ?? ''), 'fr', {
        sensitivity: 'base',
      }),
    );
  }

  /**
   * Ajoute `userInfo.isEnabled=true` pour les listes `user_firms` afin d'exclure
   * les collaborateurs désactivés, sauf si un filtre explicite ou `includeInactiveUserFirms`.
   */
  private mergeApiFiltersForRequest(): Record<string, any> {
    const merged = { ...(this.apiFilters ?? {}) };
    if (
      this.includeInactiveUserFirms ||
      !this.isUserFirmsResourceUrl()
    ) {
      return merged;
    }
    if (merged['userInfo.isEnabled'] === undefined) {
      merged['userInfo.isEnabled'] = true;
    }
    return merged;
  }

  /**
   * Crée les paramètres de requête sous forme de chaîne.
   */
  private buildQueryParams(query: string): string {
    const queryParams: Record<string, Set<string>> = {};

    // Ajouter les paramètres spécifiques
    this.addSearchParam(queryParams, query);

    // Ajouter les filtres définis
    this.addFilterParams(queryParams);

    // Convertir les paramètres en une chaîne de requête
    return this.convertQueryParamsToString(queryParams);
  }

  /**
   * Ajoute le paramètre de recherche.
   */
  private addSearchParam(
    queryParams: Record<string, Set<string>>,
    query: string
  ): void {
    if (this.searchKey && query) {
      queryParams[this.searchKey] = new Set([query]);
    }
  }

  /**
   * Ajoute les filtres personnalisés.
   */
  private addFilterParams(queryParams: Record<string, Set<string>>): void {
    if (Object.keys(this.apiFilterKeys).length > 0) {
      Object.keys(this.apiFilterKeys).forEach((key) => {
        const value = this.apiFilterKeys[key];
        if (Array.isArray(value)) {
          if (!queryParams[key]) queryParams[key] = new Set();
          value.forEach((val) => queryParams[key].add(val.toString()));
        } else if (value) {
          queryParams[key] = new Set([value.toString()]);
        }
      });
    }
  }

  /**
   * Convertit les paramètres en chaîne de requête.
   */
  private convertQueryParamsToString(
    queryParams: Record<string, Set<string>>
  ): string {
    return Object.entries(queryParams)
      .map(([key, values]) =>
        Array.from(values)
          .map((val) => `${key}=${encodeURIComponent(val)}`)
          .join('&')
      )
      .join('&');
  }

  formControlValuesInclude(value: any): boolean {
    const values = Array.isArray(this.value) ? this.value : [this.value];
    return values.some((v) => this.valueMatchesOption(v, value));
  }

  /** Compare id numérique / chaîne / IRI entre modèle et option */
  private valueMatchesOption(modelVal: any, optionVal: any): boolean {
    if (modelVal === optionVal) return true;
    if (modelVal == null || optionVal == null) return false;
    return String(modelVal) === String(optionVal);
  }

  fetchOptionsSuccess(response: any) {
    this.isLoading = false;
    this.items = response.options || [];
    this.filteredItems = response.options || [];
    this.totalItems = response.totalItems || 0;
    // Réappliquer la valeur du modèle une fois les options chargées (writeValue peut avoir eu lieu trop tôt)
    if (this._value != null && (Array.isArray(this._value) ? this._value.length > 0 : true)) {
      this.setDefaultValue();
    }
    this.updateDropdownDirection();
  }

  private ensureSelectedUserFirmOptions(): void {
    if (!this.isUserFirmsResourceUrl() || this.itemValueKey !== 'user') return;

    const selectedValues = Array.isArray(this.value) ? this.value : [this.value];
    const missingValues = selectedValues.filter(
      (value) =>
        value && !this.selectedUserFirmRequests.has(String(value)) &&
        !this.filteredItems.some((item) => this.valueMatchesOption(value, item.value))
    );
    if (missingValues.length === 0) return;

    missingValues.forEach((value) => {
      const requestKey = String(value);
      this.selectedUserFirmRequests.add(requestKey);
      this.subscription.add(
        this.http.get<any>(this.selectedUserFirmOptionUrl(value)).pipe(
          finalize(() => this.selectedUserFirmRequests.delete(requestKey)),
        ).subscribe({
          next: (response) => this.addSelectedUserFirmOption(response, value),
        })
      );
    });
  }

  private selectedUserFirmOptionUrl(value: any): string {
    // Resolve an existing selection by identity, independently of the selectable list filters.
    return `${this.apiUrl}?page=1&itemsPerPage=1&user=${encodeURIComponent(String(value))}`;
  }

  private addSelectedUserFirmOption(response: any, value: any): void {
    const member = response?.['hydra:member'] ?? response?.['member'] ?? [];
    const item = Array.isArray(member) ? member[0] : null;
    if (!item) return;

    const option = {
      value: item[this.itemValueKey],
      label: this.formatLabel(item[this.itemLabelKey]),
      FullItem: { ...item },
      selected: this.valueMatchesOption(value, item[this.itemValueKey]),
      disabled: !!item[this.optionDisabledKey],
      iconClass: this.itemIconClass(item),
      iconColor: this.itemIconColor(item),
    };

    if (
      !option.selected ||
      this.items.some((item) => this.valueMatchesOption(item.value, option.value))
    ) {
      return;
    }

    this.items = [...this.items, option];
    this.filteredItems = [...this.filteredItems, option];
    this.cdr.markForCheck();
  }

  fetchOptionsError() {
    this.isLoading = false;
    this.items = [];
    this.filteredItems = [];
    this.totalItems = 0;
  }

  setDefaultValue() {
    const values = Array.isArray(this.value) ? this.value : [this.value];
    this.setItemsDefaultValue(values);
    this.setFilteredItemsDefaultValue(values);
    if (this.isSearchInitialized && !this.isLoading) this.ensureSelectedUserFirmOptions();
  }

  setItemsDefaultValue(values: any) {
    this.items = this.filteredItems
      .filter((item) => values.some((v: any) => this.valueMatchesOption(v, item.value)))
      .map((item) => ({ ...item, selected: true }));
  }

  setFilteredItemsDefaultValue(values: any) {
    this.filteredItems = this.filteredItems.map((item) => ({
      ...item,
      selected: values.some((v: any) => this.valueMatchesOption(v, item.value)),
    }));
  }

  resetSelectedValue() {
    this.items = [];
    this.filteredItems = this.filteredItems.map((item) => ({
      ...item,
      selected: false,
    }));
  }

  // Écouteur d'événements pour détecter les clics à l'extérieur du composant
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.closeFromOutsideIfNeeded(event.target);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateDropdownDirection();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.updateDropdownDirection();
  }

  // Fonction de filtrage des éléments en fonction de la recherche
  filterItems(event: Event) {
    if (this.isDisabled || this.isFieldsetDisabled) {
      return;
    }
    // Récupère la valeur de recherche en la convertissant en minuscule
    const searchItem = (
      event.target as HTMLInputElement
    ).value.toLocaleLowerCase();
    this.searchValue = searchItem;
    if (this.hasInputItems()) {
      this.filteredItems = this.filterInputItems(searchItem);
      this.updateDropdownSelectedValuesIfsearchIsReset();
      this.updateDropdownDirection();
    } else {
      // Filtre les éléments par rapport au mot recherche
      this.searchSubject.next(searchItem);
    }

    this.onTouched();
  }

  // Filtre localement les `inputItems` en fonction de la recherche
  filterInputItems(query: string): any[] {
    const lowerQuery = query.toLowerCase();
    return this.inputItems
      .filter((item) =>
        item[this.itemLabelKey]?.toLowerCase().includes(lowerQuery)
      )
      .map((item) => ({
        value: item[this.itemValueKey],
        label: this.formatLabel(item[this.itemLabelKey]),
        FullItem: {...item},
        selected: this.valueOfItemInTheSelectionTable(item[this.itemValueKey]),
        disabled: !!item[this.optionDisabledKey],
        iconClass: this.itemIconClass(item),
        iconColor: this.itemIconColor(item),
      }));
  }

  valueOfItemInTheSelectionTable(value: any) {
    const item = this.items.find((item) => item.value == value);
    if (item) {
      return item.selected;
    }
    return false;
  }

  @Input() autoEmitChanges: boolean = false;
  @Input() clearable: boolean = true;
  /** Texte affiché à la place du placeholder quand design="chips" et qu'aucun élément n'est sélectionné. */
  @Input() chipsEmptyLabel: string = '';

  // Fonction pour basculer la sélection d'un élément en mode multi
  toggleMultiItem(item: any, event: MouseEvent) {
    if (this.isDisabled || this.isFieldsetDisabled) {
      return;
    }
    if (item.disabled) {
      event.stopPropagation();
      this.onDisabledSelection.emit(item.FullItem ?? item.value);
      return;
    }
    if (!this.clearable && item.selected) {
      const selectedCount = this.items.filter((i) => i.selected).length;
      if (selectedCount <= 1) return;
    }
    this.hasChangeSelection = true;
    event.stopPropagation();
    this.updateItemSeletedStatus(item);
    this.updateFilteredItemSeletedStatus(item);

    // Émettre automatiquement si autoEmitChanges est true
    if (this.autoEmitChanges && this._isInitialized) {
      const selectedItemValue = this.items
        .filter((i) => i.selected)
        .map((i) => (this.returnFullItem ? i.FullItem : i.value));
      this.onChange(selectedItemValue);
    }

    this.onTouched();
  }

  updateItemSeletedStatus(item: any) {
    if (!this.items.some((element) => element.value == item.value)) {
      this.items.push({ ...item });
    }
    this.items = this.items.map((element) => {
      if (element.value === item.value) {
        return { ...element, selected: !item.selected };
      }
      return element;
    });
  }

  updateFilteredItemSeletedStatus(item: any) {
    this.filteredItems = this.filteredItems.map((element) => {
      if (element.value === item.value) {
        return { ...element, selected: !item.selected };
      }
      return element;
    });
  }

  // Fonction pour basculer la sélection d'un élément en mode une sélection
  toggleSingleItem(item: any, event: MouseEvent) {
    if (this.isDisabled || this.isFieldsetDisabled) {
      return;
    }
    if (item.disabled) {
      event.stopPropagation();
      this.onDisabledSelection.emit(item.FullItem ?? item.value);
      return;
    }
    if (!this.clearable && item.selected) return;
    this.hasChangeSelection = true;
    event.stopPropagation(); // Empêche la propagation du clic
    this.updateSelectedItemForSingleMode(item);
    this.updateFilteredItemsForSingleMode(item);
    // Émet l'événement avec la liste des éléments sélectionnés
    if (this._isInitialized) {
      const selectedItemValue = this.items
        .filter((i) => i.selected)
        .map((i) => (this.returnFullItem ? i.FullItem : i.value));
      this.onChange(selectedItemValue[0]);
    }
    this.isDropdownVisible = false;
    this.resetSearch();
  }

  updateSelectedItemForSingleMode(item: any) {
    this.items = [{ ...item, selected: !item.selected }];
  }

  updateFilteredItemsForSingleMode(item: any) {
    this.filteredItems = this.filteredItems.map((element) => {
      if (element.value === item.value) {
        return { ...element, selected: !item.selected };
      }
      return { ...element, selected: false };
    });
  }

  // Fonction pour supprimer un élément de la sélection
  removeItem(item: any, event: MouseEvent) {
    event.stopPropagation();
    if (this.isDisabled || this.isFieldsetDisabled) {
      return;
    }
    if (!this.clearable) {
      const selectedCount = this.items.filter((i) => i.selected).length;
      if (selectedCount <= 1) return;
    }
    this.removeItemFormItemList(item);
    this.removeItemFormFilteredItems(item);
    // Émet la sélection mise à jour
    if (this.isMultiSelect) {
      // Multi-select : émettre sans fermer le dropdown pour permettre
      // de continuer à modifier la sélection
      if (this._isInitialized) {
        const selectedItemValue = this.items
          .filter((i) => i.selected)
          .map((i) => (this.returnFullItem ? i.FullItem : i.value));
        this.onChange(selectedItemValue);
      }
    } else if (this._isInitialized) {
      let selectedItemValue = this.items
        .filter((i) => i.selected)
        .map((i) => (this.returnFullItem ? i.FullItem : i.value));
      this.onChange(selectedItemValue[0] || null);
    }
  }

  removeItemFormItemList(item: any) {
    this.items = this.items.map((element) => {
      if (element.value === item.value) {
        return { ...element, selected: false };
      }
      return element;
    });
  }

  removeItemFormFilteredItems(item: any) {
    this.filteredItems = this.filteredItems.map((element) => {
      if (element.value === item.value) {
        return { ...element, selected: false };
      }
      return element;
    });
  }

  // Affiche la liste déroulante lorsqu'on clique dans le champ de recherche
  showDropdown() {
    if (this.isDisabled || this.isFieldsetDisabled) {
      return;
    }
    this.dropdownCoordinator.notifyOpened(this.coordInstanceId);
    this.updateFilteredItems();
    this.hasChangeSelection = false;
    this.isDropdownVisible = true; // Affiche la liste déroulante
    this.updateDropdownDirection();
    this.focusSearchInput();
    this.onTouched();
  }

  private focusSearchInput(): void {
    setTimeout(() => {
      if (!this.isDropdownVisible) {
        return;
      }
      const searchInput = this.searchInputs?.first?.nativeElement;
      if (!searchInput) {
        return;
      }
      searchInput.focus();
      searchInput.select();
    }, 0);
  }

  private bindOutsidePointerDown(): void {
    if (typeof document === 'undefined') {
      return;
    }
    this.outsidePointerDownHandler = (event: Event) => {
      this.closeFromOutsideIfNeeded(event.target);
    };
    // Capture=true pour rester robuste si d'autres composants stoppent la propagation.
    document.addEventListener('pointerdown', this.outsidePointerDownHandler, true);
  }

  private closeFromOutsideIfNeeded(target: EventTarget | null): void {
    const targetNode = target as Node | null;
    if (!targetNode || this.elementRef.nativeElement.contains(targetNode)) {
      return;
    }
    if (
      this.isDropdownVisible &&
      this.isMultiSelect &&
      this.hasChangeSelection
    ) {
      this.confirmSelection();
      return;
    }
    this.isDropdownVisible = false;
    this.computedDropUp = false;
    this.computedMaxHeight = this.maxHeight;
    this.resetSearch();
  }

  updateFilteredItems() {
    // Mise à jour de filteredItems avec les éléments qu'il y a dans items et qui sont pas dans filteredItems
    this.filteredItems = this.filteredItems.map((item) => {
      const existingItem = this.items.find((fi) => fi.value === item.value);
      if (existingItem) {
        return { ...existingItem };
      }
      return item;
    });

    this.filteredItems = [
      ...this.filteredItems,
      ...this.items.filter(
        (fi) => !this.filteredItems.some((item) => item.value === fi.value)
      ),
    ];
  }

  private categoryIconClass(label: string): string {
    const key = this.normalizeCategoryLabel(label);
    if (!this.isCategoryResourceUrl() && !key) {
      return '';
    }

    const icons: Record<string, string> = {
      comptabilite: 'fas fa-calculator',
      juridique: 'fas fa-balance-scale',
      social: 'fas fa-users',
      gestion: 'fas fa-chart-bar',
      fiscalite_personnelle: 'fas fa-building',
      commissariat_aux_comptes: 'fas fa-shield-alt',
      autre_commissariat: 'fas fa-landmark',
      juridique_social: 'fas fa-user-friends',
      interne: 'fas fa-cog',
    };

    return icons[key] ?? (this.isCategoryResourceUrl() ? 'fas fa-folder' : '');
  }

  private itemIconClass(item: any): string {
    return item[this.optionIconClassKey] || item.icon || this.categoryIconClass(item[this.itemLabelKey]);
  }

  private itemIconColor(item: any): string {
    return item[this.optionIconColorKey] || item.color || this.categoryIconColor(item[this.itemLabelKey]);
  }

  private categoryIconColor(label: string): string {
    const colors: Record<string, string> = {
      comptabilite: '#0d8bff',
      juridique: '#9b1fe8',
      social: '#f26f55',
      gestion: '#ff9f1c',
      fiscalite_personnelle: '#4bbbd8',
      commissariat_aux_comptes: '#8957e5',
      autre_commissariat: '#8e44cc',
      juridique_social: '#b04bd8',
      interne: '#d4a900',
    };

    return colors[this.normalizeCategoryLabel(label)] ?? '#607085';
  }

  private normalizeCategoryLabel(label: string): string {
    return String(label ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  }

  private isCategoryResourceUrl(): boolean {
    return !!this.apiUrl && this.apiUrl.includes('/categories');
  }

  trackByLabel(index: number, item: any): string {
    // Utiliser un identifiant unique : combinaison de label et valeur, ou index si non disponible
    if (item && item.value !== undefined) {
      return `${item.value}_${item.label || index}`;
    }
    return item?.label || `item_${index}`;
  }

  hasSelectedItem(): boolean {
    for (let index = 0; index < this.items.length; index++) {
      if (this.items[index].selected) return true;
    }
    return false;
  }

  canRemoveChip(): boolean {
    if (this.clearable) return true;
    if (!this.isMultiSelect) return false;
    const selectedCount = this.items.filter((i) => i.selected).length;
    return selectedCount > 1;
  }

  validateButtonTitle(): string {
    let count = 0;
    for (let index = 0; index < this.items.length; index++) {
      if (this.items[index].selected) count++;
    }
    if (count == 1) return 'Valider la sélection (' + count + ')';
    else if (count > 1) return 'Valider la sélections (' + count + ')';
    return 'Valider la sélection';
  }

  resetSearch(): void {
    if (this.searchValue.trim().length > 0) {
      this.searchValue = '';
      if (this.hasInputItems()) {
        this.filteredItems = this.filterInputItems('');
        this.updateDropdownSelectedValuesIfsearchIsReset();
      } else {
        this.searchSubject.next('');
      }
    }
  }

  confirmSelection(): void {
    if (this.isDisabled || this.isFieldsetDisabled) {
      return;
    }
    // Émet l'événement avec la liste des éléments sélectionnés
    if (this._isInitialized) {
      const selectedItemValue = this.items
        .filter((i) => i.selected)
        .map((i) => (this.returnFullItem ? i.FullItem : i.value));
      this.onChange(selectedItemValue);
    }
    this.isDropdownVisible = false;
    this.computedDropUp = false;
    this.computedMaxHeight = this.maxHeight;
    this.resetSearch();
    // Signale que la sélection est finalisée (distinct de onSelectionChange
    // qui s'émet à chaque modification intermédiaire)
    this.onValidateSelection.emit();
  }

  observeFieldsetDisabled() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    const fieldsets: HTMLElement[] = [];
    let parent: HTMLElement | null = this.el.nativeElement.parentElement;

    while (parent) {
      if (parent.tagName === 'FIELDSET') {
        fieldsets.push(parent as HTMLElement);
      }
      parent = parent.parentElement;
    }

    const sync = () => {
      const next = fieldsets.some((fs) => fs.hasAttribute('disabled'));
      if (next !== this.isFieldsetDisabled) {
        this.isFieldsetDisabled = next;
        this.cdr.detectChanges();
      }
    };

    sync();

    if (fieldsets.length === 0) {
      return;
    }

    this.observer = new MutationObserver(() => sync());
    for (const fs of fieldsets) {
      this.observer.observe(fs, {
        attributes: true,
        attributeFilter: ['disabled'],
      });
    }
  }

  ngAfterViewInit(): void {
    // L'ouverture automatique est désormais gérée par le composant parent
    // via @ViewChildren pour garantir un timing correct.
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.observer) {
      this.observer.disconnect();
    }
    if (typeof document !== 'undefined' && this.outsidePointerDownHandler) {
      document.removeEventListener('pointerdown', this.outsidePointerDownHandler, true);
      this.outsidePointerDownHandler = null;
    }
  }

  private updateDropdownDirection(): void {
    if (!this.isDropdownVisible) {
      return;
    }

    if (this.dropUp) {
      this.computedDropUp = true;
      this.computedMaxHeight = this.maxHeight;
      return;
    }

    if (!this.autoDropUp || typeof window === 'undefined') {
      this.computedDropUp = false;
      this.computedMaxHeight = this.maxHeight;
      return;
    }

    const rect = this.el.nativeElement.getBoundingClientRect();
    const viewportPadding = 12;
    const clippingAncestor = this.findClippingAncestor();
    const containerRect = clippingAncestor
      ? clippingAncestor.getBoundingClientRect()
      : null;
    const containerTop = Math.max(0, containerRect?.top ?? 0);
    const containerBottom = Math.min(window.innerHeight, containerRect?.bottom ?? window.innerHeight);
    // Inline menus are already included in the host height.
    const menuTop = this.inlineList ? rect.top : rect.bottom;
    const spaceBelow = containerBottom - menuTop - viewportPadding;
    const spaceAbove = rect.top - containerTop - viewportPadding;

    const optionCount = this.filteredItems?.length || this.items?.length || 0;
    const visibleOptions = Math.max(3, Math.min(optionCount || 6, 8));
    const optionRowHeight = this.isMultiSelect ? 40 : 38;
    const fixedHeaderHeight = this.isMultiSelect ? 96 : 56; // recherche + bordures
    const estimatedMenuHeight = Math.min(
      this.maxHeight,
      fixedHeaderHeight + visibleOptions * optionRowHeight
    );

    const notEnoughBelow = spaceBelow < Math.min(estimatedMenuHeight, 220);
    this.computedDropUp = !this.inlineList && notEnoughBelow && spaceAbove > spaceBelow;
    const availableSpace = this.computedDropUp ? spaceAbove : spaceBelow;
    this.computedMaxHeight = Math.max(
      140,
      Math.min(this.maxHeight, Math.floor(availableSpace))
    );
  }

  private findClippingAncestor(): HTMLElement | null {
    let parent = this.el.nativeElement.parentElement as HTMLElement | null;

    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      const overflow = style.overflow;
      const isClippingContainer =
        ['auto', 'scroll', 'hidden', 'clip'].includes(overflowY) ||
        ['auto', 'scroll', 'hidden', 'clip'].includes(overflow);

      if (isClippingContainer) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return null;
  }
}
